import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import {
  Breadcrumb,
  CountText,
  NotificationType,
  TextTooltip,
  Toolbar,
} from "@transfr-inc/dashboard-components";
import { Button } from "@transfr-inc/dashboard-components/forms";
import { PageHeader } from "@transfr-inc/dashboard-components/layouts";
import { formatDateDigits } from "@transfr-inc/dashboard-components/utils";

import { ROOT_PATH, ROUTES_CONFIG } from "@config/routes.config";
import {
  INTEGRATION_PROVIDERS,
  staticConnectionProviderNames,
} from "@constants/integration-provider.model";
import { getOrgTypeName, MULTI_ORG_CHILD } from "@constants/organization-types";

import { EditOrganizationModal, NewClientUserModal } from "@components/modals";
import { WarningMessages } from "@transfr-inc/dashboard-components";
import { SessionContext } from "@context/session.context";
import { useNotification } from "@hooks/use-notification";
import { services } from "@src/dependencies";
import { useEditWarningModal } from "@transfr-inc/dashboard-components/hooks";
import { useApiRequest } from "@src/utils/http-client";

import SyncIntegrationModal from "./sync-integration.modal";

import "./page.header.scss";

export const OrgDetailPageHeader = ({
  organization,
  breadcrumb,
  onUserCreated,
  onOrgUpdated,
  parentOrg,
  onParentChange,
}) => {
  const history = useHistory();
  const [openCreateUserModal, setOpenCreateUserModal] = useState();
  const [openEditModal, setOpenEditModal] = useState();
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const { features, currentUser } = useContext(SessionContext);
  const { canCreateClientUser, canEditOrg } = features;
  const { organizationService, integrationService } = services;
  const providerName = INTEGRATION_PROVIDERS[organization?.orgIdp?.typeId];
  const shouldShowSync = staticConnectionProviderNames.includes(providerName);
  const { addNotification } = useNotification();

  const breadcumbConfig = useMemo(() => {
    const isNewTab = history.length == 1;
    const organizationsRoute = `${ROOT_PATH}${ROUTES_CONFIG.ORGANIZATIONS.path}`;

    return {
      label: isNewTab ? "All Organizations" : breadcrumb,
      onClick: isNewTab
        ? () => history.push(organizationsRoute)
        : history.goBack,
    };
  }, [breadcrumb]);

  const { response: orgData, sendRequest: getOrgDetails } = useApiRequest(
    () => organizationService.getOrganizationDetails(organization?.code),
    false,
  );

  useEffect(() => {
    if (organization?.code) {
      getOrgDetails();
    }
  }, [organization?.code]);

  const { sendRequest: rollbackOrgData } = useApiRequest(
    () => integrationService.rollbackOrgData([organization?.code]),
    false,
  );

  // Create New User
  const onCreateNewUserClick = () => {
    setOpenCreateUserModal(true);
  };

  const onCreateNewUserClose = () => {
    setOpenCreateUserModal();
  };

  const onUserCreatedHandler = (message) => {
    setOpenCreateUserModal();
    onUserCreated && onUserCreated(message);
  };

  // Edit Organization
  const onOpenEditOrg = async () => {
    setOpenEditModal(true);
  };

  const onCloseEditOrg = () => {
    setOpenEditModal(false);
  };

  const onUpdateOrganization = (data) => {
    setOpenEditModal();
    onOrgUpdated && onOrgUpdated(data);
  };

  const rollbackRosterData = async () => {
    await rollbackOrgData([organization.code]);
  };

  const [warningEditModal, openWarningEditModal] = useEditWarningModal({
    title: WarningMessages.rollback.title,
    message: WarningMessages.rollback.message,
    buttons: [
      {
        id: "no",
        label: "No, Cancel",
      },
      {
        id: "yes",
        label: "Yes, I'm Sure",
        handler: rollbackRosterData,
        primary: true,
      },
    ],
  });

  const getOrgData = (parentOrg) => {
    if (orgData?.typeId == MULTI_ORG_CHILD.id) {
      orgData.parent = parentOrg;
    }
    return orgData;
  };

  const initiateSync = () => {
    integrationService
      .syncByProvider(providerName, currentUser.id, organization.code)
      .then(() => {
        addNotification({
          type: NotificationType.success,
          message: `The sync process for your integration has been initiated.`,
          icon: ["fa-solid", "circle-check"],
          ttl: 10000,
        });
      })
      .catch(() => {
        addNotification({
          type: NotificationType.error,
          message: `An error occurred and the sync attempt failed. Please try again.`,
          icon: ["fa-regular", "fa-triangle-exclamation"],
          ttl: 10000,
        });
      })
      .finally(() => {
        setSyncModalOpen(false);
      });
  };

  const toolbar = (
    <Toolbar>
      {canEditOrg && organization?.integrations && (
        <Button
          icon={["fa-solid", "undo"]}
          primary
          title={"Rollback"}
          label={"Rollback"}
          onClick={openWarningEditModal}
        />
      )}
      {shouldShowSync && (
        <TextTooltip
          text={"Sync Integration"}
          theme="dropdown"
          position="bottom-middle"
        >
          <Button
            icon={["fa-solid", "rotate"]}
            title={"Sync Integrations"}
            onClick={() => setSyncModalOpen(true)}
          />
        </TextTooltip>
      )}
      {canEditOrg && (
        <Button
          icon={["fa-regular", "pen-to-square"]}
          title={"Edit Organization"}
          onClick={onOpenEditOrg}
        />
      )}
      {canCreateClientUser && (
        <Button
          icon={["fa-regular", "user-plus"]}
          primary
          title={"Create User"}
          onClick={onCreateNewUserClick}
        />
      )}
    </Toolbar>
  );

  const subtitle = (
    <ul className="metadata">
      <li>Code: {organization?.code ?? "..."}</li>
      <li>Type: {getOrgTypeName(organization?.typeId) ?? "..."}</li>
      {organization?.orgExternalIds?.length > 0 && (
        <li>
          External ID:{" "}
          {organization.orgExternalIds.map((id, i) => (i == 0 ? id : ""))}
        </li>
      )}
      {organization?.expiration && (
        <li>Expiration: {formatDateDigits(organization?.expiration)}</li>
      )}
      {organization?.seats > 0 && (
        <li>
          Trainee Limit: <CountText count={organization?.seats}></CountText>
        </li>
      )}
    </ul>
  );

  return (
    <>
      <PageHeader
        title={organization?.name ?? "Loading..."}
        subTitle={subtitle}
        breadcrumb={<Breadcrumb {...breadcumbConfig} />}
        drawerColorEnabled
        className={"detail"}
      >
        {toolbar}
      </PageHeader>
      <NewClientUserModal
        organization={organization}
        open={openCreateUserModal}
        onClose={onCreateNewUserClose}
        onUserCreated={onUserCreatedHandler}
      ></NewClientUserModal>
      <EditOrganizationModal
        open={openEditModal}
        onClose={onCloseEditOrg}
        organization={getOrgData(parentOrg)}
        onUpdateOrg={onUpdateOrganization}
        onParentChange={onParentChange}
      ></EditOrganizationModal>
      <SyncIntegrationModal
        isOpen={syncModalOpen}
        onClose={() => setSyncModalOpen(false)}
        initiateSync={initiateSync}
        providerName={providerName}
      />
      {warningEditModal}
    </>
  );
};
